import { useEffect, useRef, useState } from "react";
import { Send, X } from "lucide-react";
import { api } from "../lib/api";
import { Spinner } from "./ui";

interface Msg {
  role: "user" | "assistant";
  content: string;
}

interface CopilotWidgetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** AI copilot panel — opened from the bottom navbar. */
export function CopilotWidget({ open, onOpenChange }: CopilotWidgetProps) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const message = input.trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const res = await api<{ conversationId: string; reply: string }>("/ai/chat/bo", {
        body: { conversationId, message },
      });
      setConversationId(res.conversationId);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: (e as Error).message }]);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="glass fixed right-4 z-[70] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl shadow-2xl"
      style={{
        bottom: "calc(var(--bottom-nav-height) + 12px)",
        height: "min(480px, calc(100vh - var(--header-height) - var(--tabbar-height) - var(--bottom-nav-height) - 2rem))",
      }}
    >
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <div>
          <p className="font-display text-xs font-bold">AI Copilot</p>
          <p className="text-[10px] text-white/40">Sales, stock and replenishment insights</p>
        </div>
        <button
          onClick={() => onOpenChange(false)}
          className="text-white/50 hover:text-white"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {messages.length === 0 && (
          <div className="space-y-2 pt-4 text-center">
            <p className="text-xs text-white/50">Try asking:</p>
            {["What are today's sales?", "Which products are low on stock?", "Top sellers this week?"].map((q) => (
              <button
                key={q}
                onClick={() => setInput(q)}
                className="block w-full rounded-xl bg-white/5 px-3 py-2 text-left text-xs text-white/70 hover:bg-white/10"
              >
                {q}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-xs leading-relaxed ${
              m.role === "user" ? "ml-auto bg-gradient-brand text-white" : "bg-white/8 text-white/85"
            }`}
          >
            {m.content}
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-white/40">
            <Spinner className="h-3.5 w-3.5" /> Thinking…
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-line p-3">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void send()}
            placeholder="Ask about your business…"
            className="flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs outline-none focus:border-brand-pink/60"
          />
          <button
            onClick={() => void send()}
            disabled={busy || !input.trim()}
            className="flex items-center justify-center rounded-xl bg-gradient-brand px-3.5 disabled:opacity-40"
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
